/**
 * Standalone setup-card view behind the short `/card/:employeeId` link.
 *
 * Read-only by design: it shows whatever code is currently live, and never
 * mints a new one — opening the link must not invalidate the card the worker
 * is already holding. Issuing a fresh code stays on the Field Users page.
 */
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, ArrowLeft } from 'lucide-react';

import { fieldUserApi } from '@/features/auth/api';
import { SmartLoading } from '@/lib/ui';
import { useAuthStore } from '@/stores/auth-store';
import { CredentialCard } from '../components/CredentialCard';
import { cardUrl } from '../card-url';

export function CardPage() {
  const { employeeId = '' } = useParams();
  const navigate = useNavigate();
  const tenantName = useAuthStore((s) => s.tenant?.tenantName) ?? '';

  const { data, isLoading, error } = useQuery({
    queryKey: ['field-user-card', employeeId],
    queryFn: () => fieldUserApi.cardByEmployeeId(employeeId),
    enabled: !!employeeId,
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="grid min-h-[60vh] place-items-center">
        <SmartLoading label="Loading card…" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <Shell>
        <p className="flex items-start gap-2 rounded-lg border border-danger/40 bg-danger/10 px-3 py-2.5 text-sm text-danger">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>
            No field user with employee ID <span className="font-mono">{employeeId}</span> in this
            workspace.
          </span>
        </p>
      </Shell>
    );
  }

  if (!data.code || !data.expires_at) {
    return (
      <Shell>
        <div className="rounded-lg border border-border bg-surface p-6 text-center">
          <p className="font-medium">{data.full_name}</p>
          <p className="mt-0.5 font-mono text-xs text-muted-foreground">{data.employee_id}</p>
          <p className="mt-4 text-sm text-muted-foreground">
            No live setup card. Use <strong>Generate card</strong> on the Field Users page to issue
            a provisioning code.
          </p>
          <Link to="/field-users" className="btn-primary mt-4 inline-flex">
            Go to Field Users
          </Link>
        </div>
      </Shell>
    );
  }

  return (
    <CredentialCard
      tenantName={tenantName}
      fullName={data.full_name}
      employeeId={data.employee_id}
      code={data.code}
      expiresAt={data.expires_at}
      shareUrl={cardUrl(data.employee_id)}
      onClose={() => navigate('/field-users')}
    />
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="page mx-auto max-w-md">
      <Link
        to="/field-users"
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Field users
      </Link>
      {children}
    </div>
  );
}
